import React from 'react';
import { useHistory } from 'react-router-dom';
import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid, { GridSize } from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { muiColorType } from '../../hooks';
import { navigatorTypes } from '../../routing';

export interface ActionButtonProps extends CheckGridButtonProps {
    to?: navigatorTypes;
}

export const ActionButton: React.FC<ActionButtonProps> = React.memo(
    ({ to, ...checkGridButtonProps }) => {
        const history = useHistory();
        const clickHandler = React.useCallback(
            () => to && history.push(to),
            [history, to]
        );
        return (
            <CheckGridButton
                {...checkGridButtonProps}
                onClick={to ? clickHandler : checkGridButtonProps.onClick}
            />
        );
    }
);

interface CheckGridButtonProps extends ButtonComponentProps {
    grid?: boolean;
    lg?: boolean | GridSize | undefined;
    md?: boolean | GridSize | undefined;
    sm?: boolean | GridSize | undefined;
    xs?: boolean | GridSize | undefined;
}

const CheckGridButton: React.FC<CheckGridButtonProps> = React.memo(
    ({ grid, xs, sm, md, lg, ...buttonComponentProps }) =>
        grid ? (
            <Grid item xs={xs ? xs : 12} sm={sm} md={md} lg={lg}>
                <ButtonComponent {...buttonComponentProps} />
            </Grid>
        ) : (
            <ButtonComponent {...buttonComponentProps} />
        )
);

interface ButtonComponentProps extends ButtonProps {
    loading?: boolean;
    loadingColor?: muiColorType;
    title?: string | any;
    disableFullWidth?: boolean;
    disableAll?: boolean;
    Icon?: React.ReactNode;
}

const ButtonComponent: React.FC<ButtonComponentProps> = React.memo(
    ({
        loading,
        loadingColor,
        title,
        disableFullWidth,
        disableAll,
        Icon,
        ...buttonProps
    }) =>
        Icon ? (
            <IconButton disabled={loading} {...buttonProps}>
                {loading ? (
                    <CircularProgress
                        color={loadingColor ? loadingColor : 'primary'}
                        size={16}
                    />
                ) : (
                    Icon
                )}
            </IconButton>
        ) : (
            <Button
                disabled={loading}
                variant='contained'
                startIcon={
                    loading && (
                        <CircularProgress
                            color={loadingColor ? loadingColor : 'primary'}
                            size={18}
                        />
                    )
                }
                fullWidth={!disableFullWidth || !disableAll}
                disableElevation={disableAll}
                disableFocusRipple={disableAll}
                disableRipple={disableAll}
                disableTouchRipple={disableAll}
                {...buttonProps}
            >
                {title}
            </Button>
        )
);
